import { useEffect, useRef, type RefObject } from 'react';

export interface UseInfiniteScrollOptions {
  /** False once the last page has been fetched — stops the observer firing. */
  hasMore: boolean;
  /** True while a page is in flight, so scrolling cannot queue duplicate loads. */
  isLoading: boolean;
  onLoadMore: () => void;
  /**
   * How far below the viewport the sentinel starts counting as visible. A
   * positive margin fetches the next page just before the user reaches the end,
   * so the grid grows without a visible stall.
   */
  rootMargin?: string;
  /**
   * The scrolling ancestor, when the list scrolls inside its own element rather
   * than with the page. Passed as a ref, not an element: the container is not
   * mounted on the first render, so reading `.current` at call time would
   * always hand the observer `null` and silently fall back to the viewport.
   */
  root?: RefObject<Element | null>;
}

/**
 * Loads the next page when a sentinel element scrolls into view.
 *
 * Returns a ref to attach to an element placed after the last item:
 *
 *   const sentinelRef = useInfiniteScroll({ hasMore, isLoading, onLoadMore });
 *   …
 *   <div ref={sentinelRef} />
 *
 * A scroll listener would be the obvious alternative and is the wrong tool:
 * it fires on every frame of a scroll and has to measure the document each
 * time. IntersectionObserver is told once what to watch and reports only when
 * the answer changes.
 *
 * The observer root defaults to the viewport, which is right for a page that
 * scrolls with the document — `AppShell` does. A list that scrolls inside its
 * own fixed-height element must pass that element as `root`: with the viewport
 * as root, `rootMargin` expands the *viewport* rect, so the prefetch margin
 * measures the wrong box and the next page only loads once the user has already
 * hit the bottom.
 *
 * Lives in `shared/` rather than in a module: it is domain-agnostic
 * infrastructure, and a second copy pasted into the next card grid is how two
 * screens end up with different load-more behaviour.
 */
export function useInfiniteScroll({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = '320px',
  root,
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Held in a ref so the observer is not torn down and rebuilt on every render
  // just because the callback identity changed.
  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMoreRef.current();
      },
      { root: root?.current ?? null, rootMargin },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
    // `root` is a ref object and stable across renders; its `.current` is read
    // inside the effect, after the container has mounted.
  }, [hasMore, isLoading, rootMargin, root]);

  return sentinelRef;
}
